import angular from 'angular'

import { once, inRange, uniqueId, constant } from 'lodash-es'
import { defaultTo, zeroIfNaN, makeOR, makeEQ, toPixel } from '../../utils/bits'

import envRaw from '../../utils/env-raw'
import browser from '../../utils/browser'
import Emitter from '../../utils/emitter'
import StrictObj from '../../utils/strict-obj'
import EmbedPosition from '../../utils/embed-position'
import attachGetters from '../../utils/attach-getters'

import { defineGetters } from '../../utils/define-getter'

import Template from './gallery-embed.component.ejs'

angular
  .module('ggs.gallery-embed')
  .factory('isEmbedContent', IsEmbedContentProvider)
  .component('ggsGalleryEmbed', {
    template: Template(StrictObj({
      LE: envRaw.liveEditMode
    })),
    controller: GalleryEmbedComponent,
    bindings: {
      ggsPost: '<',
      ggsMedia: '<',
      ggsProperties: '<',
      ggsEmbedLoadHandler: '<',
      ggsEmbedUnloadHandler: '<',
      ggsEmbedAttachCallback: '<',
      ggsContentReadyHandler: '<',
      ggsContentChangeHandler: '<',
      ggsContentAttachCallback: '<'
    }
  })

function IsEmbedContentProvider() {
  return function isEmbedContent(media) {
    return false
      || media.info.isTwitterVideo
      || media.info.isVimeoVideo
      || media.info.isYoutubeVideo
      || media.info.isVineVideo
      || media.info.isUploadVideo
      || media.info.isUploadAudio
      || media.info.isInstagramVideo
      || media.info.isFacebookVideo
      || media.info.isSoundcloudAudio
      || media.info.isTumblrVideo
  }
}

const FAIL = uniqueId('fail')
const DONE = uniqueId('done')
const REDO = uniqueId('redo')

const SIZE_404 = StrictObj({ width: 404, height: 404 })

function GalleryEmbedComponent(
  qh,
  env,
  $sce,
  $scope,
  $element,
  debugObj,
  imgQueue,
  StayInQueue,
  DefineMethod,
  DefineHandler,
  ElementPosition,
  EmbedStateMachine) {

  const $ctrl = $scope.$ctrl = attachGetters(StrictObj(this))
  const sign = `galleryEmbed[id=${$ctrl.ggsPost.id}][mi=${$ctrl.ggsMedia.media_index}][uid=${uniqueId()}]`
  debugObj(sign, $ctrl)

  $ctrl.DONE = DONE
  $ctrl.FAIL = FAIL
  $ctrl.REDO = REDO

  $ctrl.wrapM = DefineMethod(sign)
  $ctrl.wrapH = DefineHandler(sign)
  const stayInQueue = StayInQueue(sign)

  const displayedDetector = env.isIframeMode
    ? EmbedPosition.Displayed()
    : ElementPosition.Displayed($element)

  const inViewportDetector = env.isIframeMode
    ? EmbedPosition.InViewport()
    : ElementPosition.InViewport($element)

  $ctrl.useIframe = null
  $ctrl.iframeUrl = null
  $ctrl.htmlPlayerSetup = null
  $ctrl.htmlPlayerMedia = null

  $ctrl.showsThumb = false
  $ctrl.showsSpinner = false
  $ctrl.initsPlayer = false
  $ctrl.showsPlayer = false
  $ctrl.showsRetry = false

  $ctrl.htmlPlayer = null
  $ctrl.thumbStatus = null
  $ctrl.embedStatus = null

  $ctrl.isDisplayed = false
  $ctrl.isInViewport = false

  $ctrl.isActionAllowed = false
  $ctrl.isActionDebouncing = false

  const recalcState = EmbedStateMachine($ctrl)

  $ctrl.viewThumbSizeEmitter = Emitter(`${sign}:viewThumbSize`)
  $ctrl.viewThumbSize = undefined

  $ctrl.viewEmbedSizeEmitter = Emitter(`${sign}:viewEmbedSize`)
  $ctrl.viewEmbedSize = undefined

  $ctrl.thumbSize = imgQueue.lookup($ctrl.ggsMedia.file_thumb)
  $ctrl.embedSize = null

  defineGetters($ctrl, {
    ready: makeOR(
      makeEQ($ctrl.getters.thumbStatus, constant(DONE)),
      makeEQ($ctrl.getters.thumbStatus, constant(FAIL)))
  })

  // wrappers

  $ctrl.wrapM('callContentReadyHandler',
    qh.makeIf(
      $ctrl.getters.ready,
      once($ctrl.ggsContentReadyHandler)))

  $ctrl.wrapM('callContentChangeHandler',
    $ctrl.ggsContentChangeHandler)

  // $onInit

  $ctrl.wrapM('setupIframe', () => {
    $ctrl.useIframe = false
      || $ctrl.ggsMedia.info.isVimeoVideo
      || $ctrl.ggsMedia.info.isYoutubeVideo
      || $ctrl.ggsMedia.info.isVineVideo
      || $ctrl.ggsMedia.info.isSoundcloudAudio

    $ctrl.iframeUrl = $ctrl.useIframe
      && (false
        || ($ctrl.ggsMedia.info.isVimeoVideo && getVimeoIframeUrl())
        || ($ctrl.ggsMedia.info.isYoutubeVideo && getYoutubeIframeUrl())
        || ($ctrl.ggsMedia.info.isVineVideo && getVineIframeUrl())
        || ($ctrl.ggsMedia.info.isSoundcloudAudio && getSoundcloudIframeUrl()))
  })

  $ctrl.wrapM('setupHtmlPlayer', () => {
    if ($ctrl.useIframe) {
      return
    }

    const controls = ['play', 'progress', 'mute', 'volume']
    if (browser.likeMobile) controls.push('play-large')

    $ctrl.htmlPlayerSetup = {
      loop: { active: true },
      debug: false,
      controls,
      autoplay: false,
      keyboard: false,
      settings: false,
      tooltips: { controls: false, seek: false },
      fullscreen: { enabled: false, fallback: false, iosNative: false, dblclick: false },
      hideControls: $ctrl.ggsMedia.info.isSomeVideo,
      disableContextMenu: false
    }

    $ctrl.htmlPlayerMedia = {
      type: $ctrl.ggsMedia.info.isSomeVideo ? 'video' : 'audio',
      sources: [{
        src: $ctrl.ggsMedia.file,
        type: $ctrl.ggsMedia.file_type
      }],
      poster: $ctrl.ggsMedia.file_thumb
    }
  })

  $ctrl.wrapM('attachToParent', () => {
    return qh.join(
      $ctrl.ggsEmbedAttachCallback(
        $ctrl.extEmbedAllow,
        $ctrl.extEmbedDebounce,
        $ctrl.extEmbedDisallow),
      $ctrl.ggsContentAttachCallback(
        $ctrl.extContentCalcWidth,
        $ctrl.extContentCalcHeight,
        $ctrl.extContentApplySize))
  })

  $ctrl.wrapM('attachToDetectors', () => {
    displayedDetector.attach($ctrl.displayedHandler)
    inViewportDetector.attach($ctrl.inViewportHandler)
  })

  $ctrl.wrapH('$onInit',
    qh.makeJoin(
      $ctrl.setupIframe,
      $ctrl.setupHtmlPlayer,
      $ctrl.attachToParent,
      $ctrl.attachToDetectors,
      $ctrl.callContentReadyHandler))

  // $onDestroy

  $ctrl.wrapH('$onDestroy', () => {
    debugObj.remove($ctrl)
    displayedDetector.destroy()
    inViewportDetector.destroy()
  })

  // <ggs-element-position/>

  $ctrl.wrapH('displayedHandler',
    stayInQueue.make((isDisplayed) => {
      $ctrl.isDisplayed = isDisplayed
      return recalcState()
    }))

  $ctrl.wrapH('inViewportHandler',
    stayInQueue.make((isInViewport) => {
      $ctrl.isInViewport = isInViewport
      return recalcState()
    }))

  // <ggs-gallery-retry/>

  $ctrl.wrapH('retryRequestHandler', () => {
    $ctrl.embedStatus = REDO
    return recalcState()
  })

  // <ggs-frame/>

  $ctrl.wrapH('iframeLoadDoneHandler',
    stayInQueue.make((_iframePlayer) => {
      $ctrl.embedStatus = DONE
      return recalcState()
    }))

  $ctrl.wrapH('iframeLoadFailHandler',
    stayInQueue.make((_iframePlayerError) => {
      $ctrl.embedStatus = FAIL
      return recalcState()
    }))

  // <ggs-html-player/>

  $ctrl.wrapH('htmlPlayerReadyHandler',
    stayInQueue.make((htmlPlayer, embedSize) => {
      $ctrl.htmlPlayer = htmlPlayer
      $ctrl.embedSize = embedSize
      $ctrl.embedStatus = DONE
      return qh.join(
        recalcState(),
        $ctrl.callContentChangeHandler())
    }))

  $ctrl.wrapH('htmlPlayerErrorHandler',
    stayInQueue.make(() => {
      $ctrl.embedStatus = FAIL
      return recalcState()
    }))

  // <ggs-img-queue/>

  $ctrl.wrapH('thumbLoadDoneHandler', (thumbSize) => {
    $ctrl.thumbSize = thumbSize
    $ctrl.thumbStatus = DONE
    return qh.join(
      recalcState(),
      $ctrl.callContentReadyHandler(),
      $ctrl.callContentChangeHandler())
  })

  $ctrl.wrapH('thumbLoadFailHandler', () => {
    $ctrl.thumbStatus = FAIL
    return qh.join(
      recalcState(),
      $ctrl.callContentReadyHandler())
  })

  // <ggs-gallery-modal/>

  $ctrl.extContentCalcWidth = (showcaseHeight) => {
    const sourceSize = defaultTo($ctrl.thumbSize, $ctrl.embedSize, SIZE_404)
    return zeroIfNaN((sourceSize.width / sourceSize.height) * showcaseHeight)
  }

  $ctrl.extContentCalcHeight = (showcaseWidth) => {
    const sourceSize = defaultTo($ctrl.thumbSize, $ctrl.embedSize, SIZE_404)
    return zeroIfNaN((sourceSize.height / sourceSize.width) * showcaseWidth)
  }

  $ctrl.wrapM('extContentApplySize', (showcaseSize) => {
    $ctrl.viewThumbSize = calcContentSize(showcaseSize, defaultTo($ctrl.thumbSize, SIZE_404))
    $ctrl.viewEmbedSize = calcContentSize(showcaseSize, defaultTo($ctrl.embedSize, $ctrl.thumbSize, SIZE_404), $ctrl.useIframe)
    $ctrl.viewThumbSizeEmitter.emit()
    $ctrl.viewEmbedSizeEmitter.emit()
  })

  $ctrl.wrapM('extEmbedAllow',
    stayInQueue.make(() => {
      $ctrl.isActionAllowed = true
      $ctrl.isActionDebouncing = false
      return recalcState()
    }))

  $ctrl.wrapM('extEmbedDebounce',
    stayInQueue.make(() => {
      $ctrl.isActionAllowed = false
      $ctrl.isActionDebouncing = true
      return recalcState()
    }))

  $ctrl.wrapM('extEmbedDisallow',
    stayInQueue.make(() => {
      $ctrl.isActionAllowed = false
      $ctrl.isActionDebouncing = false
      return recalcState()
    }))

  // helpers

  function calcContentSize(showcaseSize, sourceSize, hardBounds = false) {
    const calcWidth = zeroIfNaN((sourceSize.width / sourceSize.height) * showcaseSize.height)
    const calcHeight = zeroIfNaN((sourceSize.height / sourceSize.width) * showcaseSize.width)

    const fillHor = inRange(showcaseSize.width - calcWidth, 0, 10)
    const fillVer = inRange(showcaseSize.height - calcHeight, 0, 10)
    const useWidth = (fillHor || fillVer) ? fillHor : (calcHeight < showcaseSize.height)

    const width = useWidth ? showcaseSize.width : (hardBounds ? Math.ceil(calcWidth) : 'auto')
    const height = !useWidth ? showcaseSize.height : (hardBounds ? Math.ceil(calcHeight) : 'auto')
    const marginTop = useWidth ? Math.round((showcaseSize.height - calcHeight) / 2) : 0
    const marginLeft = !useWidth ? Math.round((showcaseSize.width - calcWidth) / 2) : 0
    const margin = useWidth
      ? (marginTop >= 0 ? `${toPixel(marginTop)} 0` : `${toPixel(marginTop)} 0 0 0`)
      : (marginLeft >= 0 ? `0 ${toPixel(marginLeft)}` : `0 0 0 ${toPixel(marginLeft)}`)

    return { width, height, margin }
  }

  function trust(url) {
    return $sce.trustAsResourceUrl(url)
  }

  const baseVimeoIframeUrl = 'https://player.vimeo.com/video/'
  const baseYoutubeIframeUrl = 'https://www.youtube.com/embed/'
  const baseVineIframeUrl = 'https://vine.co/v/'
  const baseSoundcloudIframeUrl = 'https://w.soundcloud.com/player/?url=https://api.soundcloud.com/tracks/'

  function getVimeoIframeUrl() {
    const id = $ctrl.ggsMedia.external_id

    // https://developer.vimeo.com/player/embedding
    return trust(
        baseVimeoIframeUrl
      + id
      + '?autoplay=true&loop=true&playsinline=true')
  }

  function getYoutubeIframeUrl() {
    const id = $ctrl.ggsMedia.external_id

    // https://developers.google.com/youtube/player_parameters
    return trust(
        baseYoutubeIframeUrl
      + id
      + `?autoplay=1&playsinline=1&enablejsapi=1&loop=1&playlist=${id}`)
  }

  function getVineIframeUrl() {
    const id = $ctrl.ggsMedia.external_id

    // https://vine.co/v/MzKDtpQ1A93/embed
    return trust(
        baseVineIframeUrl
      + id
      + '/embed/simple?audio=1')
  }

  function getSoundcloudIframeUrl() {
    const id = $ctrl.ggsMedia.external_id

    // https://developers.soundcloud.com/docs/widget#parameters
    return trust(
        baseSoundcloudIframeUrl
      + id
      + '&auto_play=true&visual=true&hide_related=true')
  }
}
